<template>
  <BaseDialog
    type="basic"
    :is-open="isOpen"
    :title="$t('settings.organizationTeams.create')"
    class="md:w-full"
    @close="cancel"
  >
    <div class="flex flex-col gap-4 pb-2">
      <BaseInputField
        v-model="name"
        type="text"
        required
        :max-length="TEAM_NAME_MAX_LENGTH"
        :label="$t('settings.organizationTeams.name')"
        :error="nameError ?? ''"
      />
      <BaseMemberSelect
        v-model="memberIds"
        include-self
        display-selected="list"
        :label="$t('settings.organizationTeams.members')"
      />
    </div>
    <template #dismiss>
      <BaseTextButton color="text" size="M" @click="cancel">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton color="filled" size="M" @click="submit">{{
        $t('settings.organizationTeams.create')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>
<script setup lang="ts">
  import { ref, watch } from 'vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import BaseMemberSelect from '@/core/components/base/member-select/BaseMemberSelect.vue'
  import { TEAM_NAME_MAX_LENGTH, teamNameErrorKey } from '../models/organization-team'

  interface Props {
    isOpen: boolean
  }

  const props = defineProps<Props>()

  const emits = defineEmits<{
    (e: 'close'): void
    (e: 'create', body: { name: string; memberIds: string[] }): void
  }>()

  const name = ref<string>()
  const memberIds = ref<string[]>([])
  const nameError = ref<string | null>(null)

  watch(name, () => {
    nameError.value = null
  })

  watch(
    () => props.isOpen,
    (isOpen) => {
      if (isOpen) {
        name.value = undefined
        memberIds.value = []
        nameError.value = null
      }
    },
  )

  const cancel = () => {
    emits('close')
  }

  const submit = () => {
    const errorKey = teamNameErrorKey(name.value ?? '')
    if (errorKey) {
      nameError.value = errorKey
      return
    }
    emits('create', { name: name.value!.trim(), memberIds: memberIds.value })
  }

  const showInvalidName = () => {
    nameError.value = 'settings.organizationTeams.nameInvalid'
  }

  defineExpose({ showInvalidName })
</script>
